/**
 * User Permissions Utilities
 * 
 * Common utilities for checking user permissions and creating admin-only actions.
 * These utilities can be used across the entire application.
 */

/**
 * Check if user has admin privileges
 * @param {Object|null} user - User object from UserContext
 * @returns {boolean} - true if user has admin privileges (web_admin === 1), false otherwise
 * Handles cases where web_admin can be null, 0, '0', undefined, 1, or '1'
 * 
 * @example
 * const { user } = useUser();
 * const isAdmin = isUserAdmin(user);
 * 
 * @example
 * // In a component
 * if (isUserAdmin(user)) {
 *   // Show admin-only UI
 * }
 */
export const isUserAdmin = (user) => {
  if (!user) return false;
  
  // Explicitly check for admin value (1 or '1')
  // All other values (null, 0, '0', undefined, etc.) are treated as non-admin
  const webAdmin = user.web_admin;
  return webAdmin === 1 || webAdmin === '1';
};

/**
 * Creates an admin-only action configuration
 * Returns the action config if user is admin, undefined otherwise
 * 
 * This is a generic utility that can be used for:
 * - Toolbar create buttons
 * - Action menu items
 * - Any UI element that should only be visible to admins
 * 
 * @param {Object} actionConfig - Action configuration object
 * @param {string} actionConfig.label - Label/text for the action
 * @param {Function} actionConfig.onClick - Click handler function
 * @param {*} actionConfig... - Any other properties to include in the config
 * @param {boolean} isAdmin - Whether user has admin privileges
 * @returns {Object|undefined} - Action config if admin, undefined otherwise
 * 
 * @example
 * // For toolbar create button
 * const createButton = createAdminOnlyAction(
 *   { label: t('reports.createButton'), onClick: handleCreate },
 *   isAdmin
 * );
 * 
 * @example
 * // For action menu items
 * const editAction = createAdminOnlyAction(
 *   { key: 'edit', label: 'Edit', onClick: handleEdit, icon: EditIcon },
 *   isAdmin
 * );
 */
export const createAdminOnlyAction = (actionConfig, isAdmin) => {
  if (!isAdmin) {
    return undefined;
  }
  
  // Validate actionConfig
  if (!actionConfig || typeof actionConfig !== 'object') {
    console.error('createAdminOnlyAction: actionConfig must be an object');
    return undefined;
  }
  
  return actionConfig;
};

/**
 * Creates an admin-only button configuration for Toolbar component
 * Convenience wrapper around createAdminOnlyAction specifically for create buttons
 * 
 * @param {Function} t - Translation function
 * @param {string} labelKey - Translation key for button label
 * @param {Function} onClick - Click handler function
 * @param {boolean} isAdmin - Whether user has admin privileges
 * @returns {Object|undefined} - Button config if admin, undefined otherwise
 * 
 * @example
 * const { user } = useUser();
 * const isAdmin = isUserAdmin(user);
 * const createButton = createAdminOnlyCreateButton(
 *   t,
 *   'reports.createButton',
 *   handleCreateReport,
 *   isAdmin
 * );
 */
export const createAdminOnlyCreateButton = (t, labelKey, onClick, isAdmin) => {
  // Validate inputs
  if (typeof t !== 'function') {
    console.error('createAdminOnlyCreateButton: t must be a translation function');
    return undefined;
  }
  
  if (!labelKey || typeof labelKey !== 'string') {
    console.error('createAdminOnlyCreateButton: labelKey must be a non-empty string');
    return undefined;
  }
  
  if (typeof onClick !== 'function') {
    console.error('createAdminOnlyCreateButton: onClick must be a function');
    return undefined;
  }
  
  return createAdminOnlyAction(
    {
      label: t(labelKey),
      onClick,
    },
    isAdmin
  );
};

